import { useId, type ReactNode } from 'react';
import { Dialog } from '../../primitives/Dialog/Dialog';
import { ModalActions, type ModalActionsLayout } from '../ModalActions/ModalActions';
import { ModalHeader } from '../ModalHeader/ModalHeader';
import styles from './Modal.module.css';

/**
 * Figma: `Modal` (2013:8617). A card centred over an `Overlay`: `_Modal header`,
 * an optional `Slot`, then `_Modal actions`.
 *
 * `Type` does not become a prop, because none of its three options is a
 * different component — each is the same card with different content:
 *
 * - `Default` (2013:8717) — header and actions.
 * - `Destructive stacked left aligned` (2013:8729) → `destructive`. The only
 *   difference is the confirm button's `Hierarchy=Destructive`. Despite the
 *   name, nothing in it is stacked or differently aligned.
 * - `Dropdown` (2013:9056) → `children`. Adds the set's `Slot` between header
 *   and actions, drawn holding a dropdown.
 * - `Full width` (2068:29743, added 2026-09-20 with Custom Reports' `Configure
 *   campaigns`) → `fullWidth`. The `Dropdown` card with two differences and no
 *   third: the card's `maxWidth` is 800 rather than 400, and the `Slot` aligns
 *   its children to the start of the cross axis (`CENTER/MIN`) where `Dropdown`
 *   centres them (`CENTER/CENTER`) — which only shows on a child that hugs, like
 *   the `Tabs` the variant is drawn holding. This one *is* a prop, unlike the
 *   others, because it changes the card and not just what is in it.
 * - `Tune sentiment` (2037:9142) is not reached through here; see
 *   `TuneSentimentModal`, which composes `Dialog` directly.
 *
 * Built on the native `<dialog>` — see `Dialog` for what that adds beyond
 * Figma: focus containment, Escape, overlay click, focus return, scroll lock
 * and the enter/exit transition.
 */
export type ModalProps = {
  open: boolean;
  /** Escape, the overlay, the close button and the cancel button all call this. */
  onClose: () => void;
  title: ReactNode;
  /** Figma `Supporting text`. */
  description?: ReactNode;
  /** Figma `Featured icon`. */
  icon?: ReactNode;
  /** Figma `X close button`. On by default, as in every variant. */
  showClose?: boolean;
  closeLabel?: string;
  /** Figma `Slot` — the `Dropdown` variant's body. */
  children?: ReactNode;
  /** Omit, with `cancelLabel`, to render the card without an actions bar. */
  confirmLabel?: string;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  /** Figma `Type=Destructive stacked left aligned`. */
  destructive?: boolean;
  /** Figma `Tertiary button`. Cancels by calling `onClose`. */
  cancelLabel?: string;
  /** Figma `_Modal actions` `Type`. */
  actionsLayout?: ModalActionsLayout;
  /** Left-hand content of the `'end'` actions layout. */
  actionsLeading?: ReactNode;
  /**
   * Figma `Type=Full width` (2068:29743) — an 800-wide card whose slot aligns
   * its content to the start rather than centring it.
   */
  fullWidth?: boolean;
  /**
   * Bound the card to the viewport and scroll the slot instead of overflowing
   * it. Not in Figma, which draws a slot whose content always fits — see the
   * note in Modal.module.css for when that stops being true.
   */
  scrollBody?: boolean;
};

export function Modal({
  open,
  onClose,
  title,
  description,
  icon,
  showClose = true,
  closeLabel,
  children,
  confirmLabel,
  onConfirm,
  confirmDisabled,
  destructive,
  cancelLabel,
  actionsLayout,
  actionsLeading,
  fullWidth = false,
  scrollBody = false,
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const hasActions = confirmLabel !== undefined || cancelLabel !== undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      describedBy={description !== undefined ? descriptionId : undefined}
      className={styles.layer}
    >
      <div
        className={[styles.panel, fullWidth && styles.fullWidth, scrollBody && styles.scrollBody]
          .filter(Boolean)
          .join(' ')}
      >
        <ModalHeader
          title={title}
          description={description}
          icon={icon}
          onClose={showClose ? onClose : undefined}
          closeLabel={closeLabel}
          titleId={titleId}
          descriptionId={descriptionId}
        />
        {children !== undefined && <div className={styles.body}>{children}</div>}
        {hasActions && (
          <ModalActions
            layout={actionsLayout}
            confirmLabel={confirmLabel}
            onConfirm={onConfirm}
            confirmDisabled={confirmDisabled}
            destructive={destructive}
            cancelLabel={cancelLabel}
            onCancel={onClose}
            leading={actionsLeading}
            className={styles.actions}
          />
        )}
      </div>
    </Dialog>
  );
}
